/**
 * Clears the Shopify cart before add-to-cart when a product form is marked with
 * `data-clear-cart-before-add="true"`.
 *
 * Uses capture-phase listeners so Shrine ProductForm (main.js) still owns the add flow —
 * EXCEPT when the form is also marked `data-skip-cart="true"` (the LP "Buy Now, straight
 * to checkout" flow): Shrine's own submit-handling has been observed to silently fail to
 * attach on some pages sharing this same buy-box markup (browser does a native, non-AJAX
 * POST to /cart/add instead), which for a skip-cart form means the shopper lands on /cart
 * instead of checkout. Since Shrine's code lives on a remote CDN we can't fix directly, the
 * skip-cart case is handled entirely ourselves below: clear, add via fetch, then redirect to
 * checkout — never depending on Shrine's submit-handling to run at all.
 */

/** @type {Readonly<Record<string, string>>} */
const SELECTORS = Object.freeze({
  enabledProductForm: 'product-form[data-clear-cart-before-add="true"]',
  addToCartForm: 'form[data-type="add-to-cart-form"]',
  skipCartButton: '[data-skip-cart-button="true"]',
});

/** Elements allowed through to Shrine after cart clear (second pass). */
const retryPassElements = new WeakSet();

/** Pre-Shrine fetch reference (see cart-free-gifts.js) so our own add/clear calls
 *  don't trigger Shrine's independent cart:open/cart:refresh watcher mid-flow. */
const _fetch = window.__originalFetch || window.fetch.bind(window);

/**
 * @param {string} routePath
 * @returns {string}
 */
function toCartJsEndpoint(routePath) {
  const path = routePath.split('?')[0];
  return path.endsWith('.js') ? path : `${path.replace(/\/?$/, '')}.js`;
}

/**
 * @returns {string}
 */
function getCartClearEndpoint() {
  const routeUrl = window.routes?.cart_clear_url;

  if (typeof routeUrl === 'string' && routeUrl.length > 0) {
    return toCartJsEndpoint(routeUrl);
  }

  const shopifyRoot = window.Shopify?.routes?.root ?? '/';
  const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
  return `${normalizedRoot}cart/clear.js`;
}

/**
 * @param {Element | null | undefined} productForm
 * @returns {productForm is HTMLElement}
 */
function isClearCartEnabled(productForm) {
  return (
    productForm instanceof HTMLElement &&
    productForm.tagName === 'PRODUCT-FORM' &&
    productForm.getAttribute('data-clear-cart-before-add') === 'true'
  );
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isSkipCartEnabled(form) {
  return !!form.querySelector('[data-skip-cart="true"]');
}

/**
 * @returns {Promise<void>}
 */
async function clearShopifyCart() {
  const response = await _fetch(getCartClearEndpoint(), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: '{}',
  });

  if (!response.ok) {
    throw new Error(`Cart clear failed (${response.status})`);
  }

  document.dispatchEvent(new CustomEvent('cart:cleared'));
}

/**
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 */
async function addToCartViaFetch(form) {
  const routeUrl = window.routes?.cart_add_url;
  const endpoint = typeof routeUrl === 'string' && routeUrl.length > 0
    ? toCartJsEndpoint(routeUrl)
    : `${(window.Shopify?.routes?.root ?? '/').replace(/\/?$/, '/')}cart/add.js`;

  const response = await _fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    body: new FormData(form),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message = (data && (data.description || data.message)) || `Add to cart failed (${response.status})`;
    throw new Error(message);
  }
}

/**
 * @returns {string} root-relative checkout path + search
 */
function getStoreCheckoutUrl() {
  const raw = window.routes?.cart_url;
  if (typeof raw !== 'string' || !raw.length) return '/checkout';
  try {
    const u = new URL(raw, window.location.origin);
    u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
    return `${u.pathname}${u.search}`;
  } catch {
    return '/checkout';
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showFormError(form, message) {
  const wrap = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
  const text = wrap?.querySelector('.product-form__error-message');
  if (wrap && text) {
    text.textContent = message;
    wrap.hidden = false;
  }
}

class ClearCartBeforeAtc {
  /** @type {boolean} */
  #isClearing = false;

  /** @type {boolean} */
  #isAttached = false;

  attach() {
    if (this.#isAttached) return;

    document.addEventListener('submit', this.#onSubmit, true);
    document.addEventListener('click', this.#onClick, true);

    this.#isAttached = true;
  }

  /**
   * @param {HTMLElement} element
   * @param {() => void} resume
   * @param {boolean} [skipClear] See #clearAddThenCheckout — an upsell integration
   *   may have already added its own items for this same checkout.
   * @returns {Promise<void>}
   */
  async #clearThenResume(element, resume, skipClear) {
    if (this.#isClearing) return;

    this.#isClearing = true;

    if (!skipClear) {
      try {
        await clearShopifyCart();
      } catch (error) {
        console.error('[ClearCartBeforeAtc]', error);
      }
    }
    this.#isClearing = false;

    retryPassElements.add(element);
    resume();
  }

  /**
   * Skip-cart variant: clear, add via fetch, then redirect straight to checkout
   * ourselves — never resubmits for Shrine to pick up, since Shrine's own
   * submit-handling is exactly what's unreliable here.
   * @param {HTMLFormElement} form
   * @param {boolean} skipClear True when an upsell integration (e.g. gum-upsell-a/b.js)
   *   already added its own items to the cart for this same checkout and is now
   *   resubmitting the form — clearing here would wipe those items, leaving only
   *   the main product at checkout.
   * @returns {Promise<void>}
   */
  async #clearAddThenCheckout(form, skipClear) {
    if (this.#isClearing) return;
    this.#isClearing = true;

    const submitButtons = Array.from(form.querySelectorAll('button[type="submit"], button[name="add"]'));
    submitButtons.forEach((btn) => {
      btn.setAttribute('aria-busy', 'true');
      btn.style.pointerEvents = 'none';
      btn.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    });

    try {
      if (!skipClear) {
        await clearShopifyCart();
      }
      await addToCartViaFetch(form);
      window.location.href = getStoreCheckoutUrl();
    } catch (error) {
      console.error('[ClearCartBeforeAtc]', error);
      showFormError(form, error?.message || 'Could not add item to cart. Please try again.');
      submitButtons.forEach((btn) => {
        btn.removeAttribute('aria-busy');
        btn.style.pointerEvents = '';
        btn.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      });
    } finally {
      this.#isClearing = false;
    }
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean} `true` when the event should proceed to Shrine unchanged.
   */
  #consumeRetryPass(element) {
    if (!retryPassElements.has(element)) return false;
    retryPassElements.delete(element);
    return true;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
    if (!isClearCartEnabled(form.closest('product-form'))) return;

    if (isSkipCartEnabled(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const skipClear = form.__nwSkipNextCartClear === true;
      if (skipClear) delete form.__nwSkipNextCartClear;
      void this.#clearAddThenCheckout(form, skipClear);
      return;
    }

    if (this.#consumeRetryPass(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const { submitter } = event;
    const skipClear = form.__nwSkipNextCartClear === true;
    if (skipClear) delete form.__nwSkipNextCartClear;

    void this.#clearThenResume(form, () => {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter ?? undefined);
      } else {
        form.submit();
      }
    }, skipClear);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest(SELECTORS.skipCartButton);
    if (!(button instanceof HTMLElement)) return;
    if (!isClearCartEnabled(button.closest('product-form'))) return;

    if (this.#consumeRetryPass(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    void this.#clearThenResume(button, () => {
      button.click();
    });
  };
}

if (!window.__clearCartBeforeAtcController) {
  window.__clearCartBeforeAtcController = new ClearCartBeforeAtc();
  window.__clearCartBeforeAtcController.attach();
}
